import { useState, useMemo, useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import {
  type IntakeRecord, type IntakeDisposition, type IntegrityConfig,
  DEFAULT_INTEGRITY_CONFIG, processIncomingLead, formatPhoneDisplay,
} from "@/data/leadIntegrityEngine";
import { LeadIntegrityService } from "@/services/leadIntegrity.service";
import type { Lead } from "@/data/pipelineTypes";
import { PROJECT_TYPES } from "@/data/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { BackToLeadPerformance } from "@/components/BackToLeadPerformance";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, Search, Filter,
  CheckCircle2, XCircle, AlertTriangle, Clock, Eye, ArrowRight,
  Phone, Mail, MapPin, Layers, BarChart3, Settings2, Plus,
  FileText, Zap, RefreshCw, ChevronDown, ChevronRight, User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const DISPOSITION_CONFIG: Record<IntakeDisposition, { label: string; icon: React.ElementType; color: string }> = {
  approved: { label: "Approved", icon: CheckCircle2, color: "text-success bg-success/10 border-success/30" },
  approved_with_warning: { label: "Warning", icon: AlertTriangle, color: "text-warning bg-warning/10 border-warning/30" },
  duplicate_flagged: { label: "Duplicate", icon: Layers, color: "text-warning bg-warning/10 border-warning/30" },
  quarantined: { label: "Quarantined", icon: Clock, color: "text-primary bg-primary/10 border-primary/30" },
  rejected: { label: "Rejected", icon: XCircle, color: "text-destructive bg-destructive/10 border-destructive/30" },
};

export default function LeadIntegrityDashboard({ embedded = false }: { embedded?: boolean } = {}) {
  const { leads, setLeads, leadSources } = useAppData();
  const { role } = useRole();
  const [records, setRecords] = useState<IntakeRecord[]>([]);
  const [config, setConfig] = useState<IntegrityConfig>(DEFAULT_INTEGRITY_CONFIG);
  const [search, setSearch] = useState("");
  const [dispositionFilter, setDispositionFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selectedRecord, setSelectedRecord] = useState<IntakeRecord | null>(null);
  const [showTestForm, setShowTestForm] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [reviewNote, setReviewNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await LeadIntegrityService.getAll();
      if (cancelled) return;
      setRecords(data ?? []);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Test form state
  const [testData, setTestData] = useState({
    primaryName: "", primaryPhone: "", primaryEmail: "",
    address: "", city: "", state: "FL", zip: "",
    projectType: "", leadSource: "",
  });

  // Filter records
  const filtered = useMemo(() => {
    let result = records;
    if (dispositionFilter !== "all")
      result = result.filter((r) => r.disposition === dispositionFilter);
    if (sourceFilter !== "all")
      result = result.filter((r) => r.source === sourceFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        r.normalizedData.primaryName.toLowerCase().includes(q) ||
        r.normalizedData.primaryEmail.toLowerCase().includes(q) ||
        r.normalizedData.primaryPhone.includes(q) ||
        r.normalizedData.city.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q)
      );
    }
    return result;
  }, [records, dispositionFilter, sourceFilter, search]);

  // Stats
  const stats = useMemo(() => ({
    total: records.length,
    approved: records.filter((r) => r.disposition === "approved").length,
    warning: records.filter((r) => r.disposition === "approved_with_warning").length,
    duplicate: records.filter((r) => r.disposition === "duplicate_flagged").length,
    quarantined: records.filter((r) => r.disposition === "quarantined").length,
    rejected: records.filter((r) => r.disposition === "rejected").length,
    avgScore: records.length ? Math.round(records.reduce((s, r) => s + r.totalScore, 0) / records.length) : 0,
    deliveryRate: records.length ? Math.round((records.filter((r) => r.deliveredToPipeline).length / records.length) * 100) : 0,
  }), [records]);

  // Review queue
  const reviewQueue = useMemo(() =>
    records.filter((r) => r.disposition === "quarantined" || r.disposition === "duplicate_flagged")
      .filter((r) => !r.reviewedBy),
    [records]
  );

  const sourceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const source of leadSources) {
      const id = String(source.id ?? "").trim();
      const name = String(source.name ?? "").trim();
      if (id && name) map.set(id, name);
    }
    return map;
  }, [leadSources]);

  const sourceNameByNormalized = useMemo(() => {
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const map = new Map<string, string>();
    for (const source of leadSources) {
      const name = String(source.name ?? "").trim();
      if (!name) continue;
      map.set(normalize(name), name);
    }
    return map;
  }, [leadSources]);

  const sourceNameByAlias = useMemo(() => {
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const aliasToNames = new Map<string, Set<string>>();
    for (const source of leadSources) {
      const name = String(source.name ?? "").trim();
      if (!name) continue;
      const words = name.split(/\s+/).map((w) => w.trim()).filter(Boolean);
      const aliases = new Set<string>();
      aliases.add(normalize(name));
      if (words.length > 1) {
        aliases.add(normalize(words.map((w) => w[0]).join("")));
      }
      if (words.length === 1) {
        aliases.add(normalize(words[0].slice(0, 1)));
      }
      for (const alias of aliases) {
        if (!alias) continue;
        if (!aliasToNames.has(alias)) aliasToNames.set(alias, new Set<string>());
        aliasToNames.get(alias)!.add(name);
      }
    }
    const uniqueAliasMap = new Map<string, string>();
    for (const [alias, names] of aliasToNames.entries()) {
      if (names.size === 1) uniqueAliasMap.set(alias, [...names][0]);
    }
    return uniqueAliasMap;
  }, [leadSources]);

  const resolveCanonicalSourceName = (rawValue: string | null | undefined) => {
    const raw = String(rawValue ?? "").trim();
    if (!raw) return "Unknown";
    const byId = sourceNameById.get(raw);
    if (byId) return byId;
    const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const byName = sourceNameByNormalized.get(normalized);
    if (byName) return byName;
    const byAlias = sourceNameByAlias.get(normalized);
    return byAlias ?? "Unmapped";
  };

  function handleTestSubmit() {
    if (!testData.primaryName || !testData.primaryPhone) {
      toast.error("Name and phone are required");
      return;
    }
    const result = processIncomingLead(testData, "manual", leads, config);
    setRecords((prev) => [result, ...prev]);

    if (result.deliveredToPipeline) {
      const now = new Date().toISOString().split("T")[0];
      const newLead = {
        id: `LEAD-${String(leads.length + 1).padStart(3, "0")}`,
        displayReference: `OPP-${String(leads.length + 1).padStart(6, "0")}`,
        ...result.normalizedData,
        projectType: (PROJECT_TYPES.includes(result.normalizedData.projectType as any) ? result.normalizedData.projectType : "Other") as import("@/data/types").ProjectType,
        stage: "Lead" as const,
        stageHistory: [{ id: `sh-${Date.now()}`, fromStage: null, toStage: "Lead" as const, changedAt: now, changedBy: "System" }],
        assignedRep: "Unassigned",
        ownershipHistory: [{ id: `oh-${Date.now()}`, fromRep: null, toRep: "Unassigned", changedAt: now, changedBy: "Integrity Engine" }],
        activities: [{ id: `la-${Date.now()}`, type: "system" as const, description: `Lead accepted by Integrity Engine (Score: ${result.totalScore})`, createdAt: now, createdBy: "System" }],
        totalEstimate: 0,
        createdAt: now,
        updatedAt: now,
        notes: "",
      } as Lead;
      setLeads((prev) => [newLead, ...prev]);
      setRecords((prev) => prev.map((r) => r.id === result.id ? { ...r, createdLeadId: newLead.id } : r));
      toast.success(`Lead approved and sent to pipeline as ${newLead.id}`);
    } else {
      toast.warning(`Lead ${result.disposition}: ${result.dispositionReason}`);
    }

    setShowTestForm(false);
    setTestData({ primaryName: "", primaryPhone: "", primaryEmail: "", address: "", city: "", state: "FL", zip: "", projectType: "", leadSource: "" });
  }

  function handleReviewAction(record: IntakeRecord, action: "approve" | "reject") {
    const now = new Date().toISOString();
    if (action === "approve") {
      const nowDate = now.split("T")[0];
      const newLead = {
        id: `LEAD-${String(leads.length + 1).padStart(3, "0")}`,
        displayReference: `OPP-${String(leads.length + 1).padStart(6, "0")}`,
        ...record.normalizedData,
        projectType: (PROJECT_TYPES.includes(record.normalizedData.projectType as any) ? record.normalizedData.projectType : "Other") as import("@/data/types").ProjectType,
        stage: "Lead" as const,
        stageHistory: [{ id: `sh-${Date.now()}`, fromStage: null, toStage: "Lead" as const, changedAt: nowDate, changedBy: "System" }],
        assignedRep: "Unassigned",
        ownershipHistory: [{ id: `oh-${Date.now()}`, fromRep: null, toRep: "Unassigned", changedAt: nowDate, changedBy: "Admin Review" }],
        activities: [{ id: `la-${Date.now()}`, type: "system" as const, description: `Manually approved from review queue (Score: ${record.totalScore})`, createdAt: nowDate, createdBy: "Admin" }],
        totalEstimate: 0,
        createdAt: nowDate,
        updatedAt: nowDate,
        notes: reviewNote || "",
      } as Lead;
      setLeads((prev) => [newLead, ...prev]);
      setRecords((prev) => prev.map((r) =>
        r.id === record.id
          ? { ...r, disposition: "approved" as IntakeDisposition, reviewedBy: "Admin", reviewedAt: now, reviewNote, deliveredToPipeline: true, createdLeadId: newLead.id }
          : r
      ));
      toast.success(`Lead approved and pushed to pipeline as ${newLead.id}`);
    } else {
      setRecords((prev) => prev.map((r) =>
        r.id === record.id
          ? { ...r, disposition: "rejected" as IntakeDisposition, reviewedBy: "Admin", reviewedAt: now, reviewNote, deliveredToPipeline: false }
          : r
      ));
      toast.success("Lead rejected");
    }
    setSelectedRecord(null);
    setReviewNote("");
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header — hidden when embedded inside Lead Performance (parent owns the title) */}
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <BackToLeadPerformance />
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2 mt-1.5">
              <Shield className="h-5 w-5 text-primary" /> Lead Integrity Engine
            </h1>
            <p className="text-sm text-muted-foreground">
              Validation gateway · {stats.total} leads processed · {stats.deliveryRate}% delivery rate
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowConfig(true)}>
              <Settings2 className="h-4 w-4 mr-1" /> Rules
            </Button>
            <Button size="sm" onClick={() => setShowTestForm(true)}>
              <Plus className="h-4 w-4 mr-1" /> Test Lead
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowConfig(true)}>
            <Settings2 className="h-4 w-4 mr-1" /> Rules
          </Button>
          <Button size="sm" onClick={() => setShowTestForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> Test Lead
          </Button>
        </div>
      )}

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-1"><BarChart3 className="h-3.5 w-3.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="queue" className="gap-1">
            <Clock className="h-3.5 w-3.5" /> Review Queue
            {reviewQueue.length > 0 && <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px]">{reviewQueue.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><FileText className="h-3.5 w-3.5" /> All Records</TabsTrigger>
        </TabsList>

        {/* ─── Dashboard Tab ─── */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              { label: "Total", value: stats.total, icon: Zap, color: "text-foreground" },
              { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "text-success" },
              { label: "Warnings", value: stats.warning, icon: AlertTriangle, color: "text-warning" },
              { label: "Duplicates", value: stats.duplicate, icon: Layers, color: "text-warning" },
              { label: "Quarantined", value: stats.quarantined, icon: Clock, color: "text-primary" },
              { label: "Rejected", value: stats.rejected, icon: XCircle, color: "text-destructive" },
              { label: "Avg Score", value: stats.avgScore, icon: BarChart3, color: "text-primary" },
              { label: "Delivery %", value: `${stats.deliveryRate}%`, icon: ArrowRight, color: "text-success" },
            ].map((kpi) => (
              <Card key={kpi.label} className="border">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <kpi.icon className={cn("h-3.5 w-3.5", kpi.color)} />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{kpi.label}</span>
                  </div>
                  <p className={cn("text-lg font-bold", kpi.color)}>{kpi.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Disposition Breakdown */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Disposition Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(DISPOSITION_CONFIG).map(([key, cfg]) => {
                  const count = records.filter((r) => r.disposition === key).length;
                  const pct = records.length ? Math.round((count / records.length) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <cfg.icon className={cn("h-4 w-4 shrink-0", cfg.color.split(" ")[0])} />
                      <span className="text-sm w-24">{cfg.label}</span>
                      <Progress value={pct} className="flex-1 h-2" />
                      <span className="text-xs text-muted-foreground w-12 text-right">{count} ({pct}%)</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Source Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const sources: Record<string, number> = {};
                  records.forEach((r) => {
                    const sourceName = resolveCanonicalSourceName(r.normalizedData.leadSource || r.source);
                    const key = sourceName === "Unmapped" ? "Unmapped (needs mapping)" : sourceName;
                    sources[key] = (sources[key] || 0) + 1;
                  });
                  return Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([src, count]) => {
                    const pct = Math.round((count / records.length) * 100);
                    return (
                      <div key={src} className="flex items-center gap-3">
                        <span className="text-sm w-28 truncate">{src || "Unknown"}</span>
                        <Progress value={pct} className="flex-1 h-2" />
                        <span className="text-xs text-muted-foreground w-12 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  });
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Rejection Reasons */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Rejection & Quarantine Reasons</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(() => {
                  const reasons: Record<string, number> = {};
                  records.filter((r) => r.disposition === "rejected" || r.disposition === "quarantined")
                    .forEach((r) => { reasons[r.dispositionReason] = (reasons[r.dispositionReason] || 0) + 1; });
                  return Object.entries(reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{reason}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ));
                })()}
                {records.filter((r) => r.disposition === "rejected" || r.disposition === "quarantined").length === 0 && (
                  <p className="text-sm text-muted-foreground">No rejections or quarantines</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Review Queue Tab ─── */}
        <TabsContent value="queue" className="space-y-4">
          {reviewQueue.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <ShieldCheck className="h-10 w-10 text-success mx-auto mb-3" />
                <p className="font-medium text-foreground">Review Queue Clear</p>
                <p className="text-sm text-muted-foreground mt-1">No leads pending review</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {reviewQueue.map((record) => {
                const cfg = DISPOSITION_CONFIG[record.disposition];
                return (
                  <Card key={record.id} className="border hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={cn("text-[10px]", cfg.color)}>{cfg.label}</Badge>
                            <span className="text-xs text-muted-foreground">{displayLabel}</span>
                          </div>
                          <p className="font-medium text-foreground">{record.normalizedData.primaryName}</p>
                          <div className="flex flex-wrap items =-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>
                              {record.normalizedData.primaryEmail && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{record.normalizedData.primaryEmail}</span>}
                            </span>
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3"/>{record.normalizedData.city || "N/A"}, {record.normalizedData.zip}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="font-medium">Reason:</span> {record.disposition}
                          </p>
                          {record.duplicateMatches.length > 0 && (
                            <p className="text-xs text-warning mt-0.5">
                              Duplicate match: {record.duplicateMatches[0].matchedName} ({record.duplicateMatches[0].matchedLeadId}) — {record.duplicateMatches[0].confidence}% confidence
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-center">
                            <p className="text-lg font-bold text-foreground">{record.totalScore}</p>
                            <p className="text-[10px] text-muted-foreground">Score</p>
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => { setSelectedRecord(record); setReviewNote(""); }}>
                              <Eye className="h-3.5 w-3.5 mr-1" /> Review
                            </Button>
                            <Button size="sm" className="bg-success hover:bg-success text-white" onClick={() => handleReviewAction(record, "approve")}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleReviewAction(record, "reject")}>
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─── History Tab ─── */}
        <TabsContent value="history" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search records..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
            </div>
            <Select value={dispositionFilter} onValueChange={setDispositionFilter}>
              <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All Dispositions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dispositions</SelectItem>
                {Object.entries(DISPOSITION_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="All Sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="api">API</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="web_form">Web Form</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Records Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">ID</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Name</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Disposition</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Score</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Source</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Geo</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Fit</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Pipeline</th>
                      <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Time</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((record) => {
                      const cfg = DISPOSITION_CONFIG[record.disposition];
                      return (
                        <tr key={record.id} className="border-b hover:bg-muted/30">
                          <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{record.id}</td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-foreground">{record.normalizedData.primaryName}</p>
                            <p className="text-[10px] text-muted-foreground">{record.normalizedData.city}, {record.normalizedData.zip}</p>
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge variant="outline" className={cn("text-[10px]", cfg.color)}>{cfg.label}</Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={cn("font-bold text-sm", record.totalScore >= 80 ? "text-success" : record.totalScore >= 55 ? "text-warning" : "text-destructive")}>{record.totalScore}</span>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">{record.source}</td>
                          <td className="px-3 py-2.5">
                            <Badge variant="outline" className={cn("text-[10px]",
                              record.geoResult === "in_area" ? "text-success border-success/30" :
                              record.geoResult === "out_of_area" ? "text-destructive border-destructive/30" : "text-muted-foreground"
                            )}>
                              {record.geoResult === "in_area" ? "✓ Area" : record.geoResult === "out_of_area" ? "✗ OOA" : "?"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge variant="outline" className={cn("text-[10px]",
                              record.projectFitResult === "allowed" ? "text-success border-success/30" :
                              record.projectFitResult === "rejected" ? "text-destructive border-destructive/30" : "text-warning border-warning/30"
                            )}>
                              {record.projectFitResult === "allowed" ? "✓" : record.projectFitResult === "rejected" ? "✗" : "?"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            {record.deliveredToPipeline
                              ? <Badge className="bg-success/15 text-success border-success/30 text-[10px]">Delivered</Badge>
                              : <Badge variant="outline" className="text-[10px] text-muted-foreground">Blocked</Badge>
                            }
                          </td>
                          <td className="px-3 py-2.5 text-[10px] text-muted-foreground whitespace-nowrap">
                            {new Date(record.receivedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="px-3 py-2.5">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setSelectedRecord(record); setReviewNote(""); }}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Detail Dialog ─── */}
      <Dialog open={!!selectedRecord} onOpenChange={(open) => { if (!open) setSelectedRecord(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedRecord && (() => {
            const r = selectedRecord;
            const cfg = DISPOSITION_CONFIG[r.disposition];
            const needsReview = (r.disposition === "quarantined" || r.disposition === "duplicate_flagged") && !r.reviewedBy;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <cfg.icon className={cn("h-5 w-5", cfg.color.split(" ")[0])} />
                    Intake Record: {r.id}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Contact */}
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Contact</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{r.normalizedData.primaryName}</span></div>
                      <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{formatPhoneDisplay(r.normalizedData.primaryPhone)}</span></div>
                      <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{r.normalizedData.primaryEmail || "—"}</span></div>
                      <div><span className="text-muted-foreground">Location:</span> <span className="font-medium">{r.normalizedData.city}, {r.normalizedData.state} {r.normalizedData.zip}</span></div>
                      <div><span className="text-muted-foreground">Project:</span> <span className="font-medium">{r.normalizedData.projectType || "—"}</span></div>
                      <div><span className="text-muted-foreground">Source:</span> <span className="font-medium">{r.normalizedData.leadSource}</span></div>
                    </div>
                  </div>

                  <Separator />

                  {/* Score */}
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Quality Score: {r.totalScore}/100</h4>
                    <div className="space-y-1.5">
                      {r.scoreBreakdown.map((b) => (
                        <div key={b.factor} className="flex items-center gap-2">
                          <span className="text-xs w-32 text-muted-foreground">{b.factor}</span>
                          <Progress value={(b.points / b.max) * 100} className="flex-1 h-1.5" />
                          <span className="text-xs font-mono w-10 text-right">{b.points}/{b.max}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Validation */}
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Validations</h4>
                    <div className="space-y-1">
                      {r.validationResults.map((v, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {v.status === "valid" && <CheckCircle2 className="h-3 w-3 text-success" />}
                          {v.status === "suspicious" && <AlertTriangle className="h-3 w-3 text-warning" />}
                          {v.status === "invalid" && <XCircle className="h-3 w-3 text-destructive" />}
                          {v.status === "missing" && <XCircle className="h-3 w-3 text-muted-foreground" />}
                          <span className="text-muted-foreground">{v.field}:</span>
                          <span>{v.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Duplicates */}
                  {r.duplicateMatches.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Duplicate Matches</h4>
                        {r.duplicateMatches.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-warning/10 border border-warning/30 rounded p-2 mb-1">
                            <Layers className="h-3 w-3 text-warning" />
                            <span>{d.type.replace(/_/g, " ")} — {d.matchedName} ({d.matchedLeadId}) — {d.confidence}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <Separator />

                  {/* Decision */}
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Decision</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={cn(cfg.color)}>{cfg.label}</Badge>
                      <span className="text-sm text-muted-foreground">{r.dispositionReason}</span>
                    </div>
                    {r.reviewedBy && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Reviewed by {r.reviewedBy} at {new Date(r.reviewedAt!).toLocaleString()}
                        {r.reviewNote && <> — "{r.reviewNote}"</>}
                      </p>
                    )}
                  </div>

                  {/* Review actions */}
                  {needsReview && (
                    <>
                      <Separator />
                      <div>
                        <Label className="text-xs">Review Note</Label>
                        <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Optional note..." className="mt-1 h-16" />
                        <div className="flex gap-2 mt-3">
                          <Button className="flex-1 bg-success hover:bg-success text-white" onClick={() => handleReviewAction(r, "approve")}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Push to Pipeline
                          </Button>
                          <Button variant="destructive" className="flex-1" onClick={() => handleReviewAction(r, "reject")}>
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ─── Test Lead Dialog ─── */}
      <Dialog open={showTestForm} onOpenChange={setShowTestForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" /> Test Lead Intake
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={testData.primaryName} onChange={(e) => setTestData({ ...testData, primaryName: e.target.value })} className="h-9" placeholder="Full name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input value={testData.primaryPhone} onChange={(e) => setTestData({ ...testData, primaryPhone: e.target.value })} className="h-9" placeholder="(813) 555-1234" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={testData.primaryEmail} onChange={(e) => setTestData({ ...testData, primaryEmail: e.target.value })} className="h-9" placeholder="email@example.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={testData.address} onChange={(e) => setTestData({ ...testData, address: e.target.value })} className="h-9" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={testData.city} onChange={(e) => setTestData({ ...testData, city: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={testData.state} onChange={(e) => setTestData({ ...testData, state: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">ZIP *</Label>
                <Input value={testData.zip} onChange={(e) => setTestData({ ...testData, zip: e.target.value })} className="h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Project Type</Label>
                <Select value={testData.projectType} onValueChange={(v) => setTestData({ ...testData, projectType: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {[...PROJECT_TYPES, "Roofing", "HVAC"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lead Source</Label>
                <Select value={testData.leadSource} onValueChange={(v) => setTestData({ ...testData, leadSource: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {["Google Ads", "Facebook Ads", "Referral", "Home Show", "Website", "TV Ad", "Angi", "Other"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestForm(false)}>Cancel</Button>
            <Button onClick={handleTestSubmit}>
              <Zap className="h-4 w-4 mr-1" /> Process Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Config Dialog ─── */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" /> Integrity Engine Rules
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Score Thresholds</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">High Quality ≥</Label>
                  <Input type="number" value={config.scoreThresholds.high} className="h-9"
                    onChange={(e) => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, high: parseInt(e.target.value) || 80 } })} />
                </div>
                <div>
                  <Label className="text-xs">Medium Quality ≥</Label>
                  <Input type="number" value={config.scoreThresholds.medium} className="h-9"
                    onChange={(e) => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, medium: parseInt(e.target.value) || 55 } })} />
                </div>
                <div>
                  <Label className="text-xs">Low Quality ≥</Label>
                  <Input type="number" value={config.scoreThresholds.low} className="h-9"
                    onChange={(e) => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, low: parseInt(e.target.value) || 30 } })} />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Duplicate Window</h4>
              <div className="flex items-center gap-2">
                <Input type="number" value={config.duplicateWindowHours} className="h-9 w-24"
                  onChange={(e) => setConfig({ ...config, duplicateWindowHours: parseInt(e.target.value) || 72 })} />
                <span className="text-sm text-muted-foreground">hours</span>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Allowed Project Types</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.allowedProjectTypes.map((t) => (
                  <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Rejected Project Types</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.rejectedProjectTypes.map((t) => (
                  <Badge key={t} variant="outline" className="text-xs text-destructive border-destructive/30">{t}</Badge>
                ))}
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Approved Service Areas ({config.approvedZipCodes.length} ZIP codes)</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.approvedCities.map((c) => (
                  <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
